import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Search, Loader2, Calendar, Receipt } from 'lucide-react';
import { inputClass } from './caseConstants';

// Search for an existing booking or order to attach to a case. Results are
// narrowed to the customer's own records where we know their email, and can be
// filtered further by invoice number, product or date.
export default function AttachLinkModal({ caseData, onClose, onDone }) {
  const [mode, setMode] = useState('booking');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const email = caseData.customer_email || '';

  const { data: bookings = [], isLoading: loadingBookings } = useQuery({
    queryKey: ['attach-bookings', email],
    queryFn: () =>
      email
        ? base44.entities.Booking.filter({ customer_email: email }, '-date', 50)
        : base44.entities.Booking.list('-date', 50),
    enabled: mode === 'booking',
  });

  const { data: orders = [], isLoading: loadingOrders } = useQuery({
    queryKey: ['attach-orders', email],
    queryFn: () =>
      email
        ? base44.entities.Order.filter({ customer_email: email }, '-created_date', 50)
        : base44.entities.Order.list('-created_date', 50),
    enabled: mode === 'order',
  });

  const term = q.trim().toLowerCase();
  const filteredBookings = bookings.filter(
    (b) =>
      !term ||
      [b.date, b.time_slot, b.experience_type, b.rig_type, b.status]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
  );
  const filteredOrders = orders.filter(
    (o) =>
      !term ||
      [o.invoice_number, o.product_name, o.status]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
  );

  const attach = async (payload) => {
    setBusy(payload.bookingId || payload.orderId);
    setError('');
    try {
      const res = await base44.functions.invoke('caseLink', { caseId: caseData.id, ...payload });
      if (res.data?.error) throw new Error(res.data.error);
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not attach the record.');
    }
    setBusy('');
  };

  const loading = mode === 'booking' ? loadingBookings : loadingOrders;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-lg border border-white/10 bg-[#0d0d0f] max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-1 border-b border-white/5">
          {[
            { id: 'booking', label: 'BOOKING', icon: Calendar },
            { id: 'order', label: 'ORDER', icon: Receipt },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setMode(t.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-[10px] font-bold tracking-wider border-b-2 -mb-px transition-colors ${
                  mode === t.id ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
          <button
            onClick={onClose}
            className="ml-auto mr-4 text-white/40 hover:text-white text-[10px] font-bold tracking-widest transition-colors"
          >
            CLOSE
          </button>
        </div>

        <div className="p-4 border-b border-white/5">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={mode === 'booking' ? 'Filter by date, slot or bay…' : 'Filter by invoice, product or status…'}
              className={`${inputClass} pl-9`}
            />
          </div>
          {!email && (
            <p className="text-white/25 text-[10px] mt-2">
              This case has no customer email, so the most recent records are shown.
            </p>
          )}
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-2">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : (mode === 'booking' ? filteredBookings : filteredOrders).length === 0 ? (
            <p className="text-white/30 text-xs py-10 text-center">No matching records.</p>
          ) : mode === 'booking' ? (
            filteredBookings.map((b) => (
              <button
                key={b.id}
                onClick={() => attach({ bookingId: b.id })}
                disabled={!!busy}
                className="w-full text-left border border-white/5 hover:border-primary/40 bg-[#111115] px-3 py-2.5 transition-colors disabled:opacity-50"
              >
                <p className="text-white text-xs">
                  {b.date || 'No date'} · {b.time_slot || '—'}
                </p>
                <p className="text-white/40 text-[11px] mt-0.5 truncate">
                  {b.experience_type || 'Session'} · {b.rig_type || 'bay TBC'} · {b.status || 'unknown'}
                </p>
              </button>
            ))
          ) : (
            filteredOrders.map((o) => (
              <button
                key={o.id}
                onClick={() => attach({ orderId: o.id })}
                disabled={!!busy}
                className="w-full text-left border border-white/5 hover:border-primary/40 bg-[#111115] px-3 py-2.5 transition-colors disabled:opacity-50"
              >
                <p className="text-white text-xs">
                  {o.invoice_number || 'No invoice number'} · £{Number(o.amount || 0).toFixed(2)}
                </p>
                <p className="text-white/40 text-[11px] mt-0.5 truncate">
                  {o.product_name || 'Order'} · {o.status || 'unknown'}
                </p>
              </button>
            ))
          )}

          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      </div>
    </div>
  );
}